import React, { useState, useEffect } from 'react'
import { Autocomplete, PLLSearchResult, ShopifySearchResult } from './index'
import { SEARCH_CONFIG } from '../../Config/config'
import { PLLContentSearchResult } from './PLLSearchResult'
import { SearchLoadingContainer, SearchLoadingText } from './styles'
import { debouncePromise } from '../../Utils'

type AlgoliaModules = {
  getAlgoliaResults: any
  searchClient: any
}

export function AutocompleteContainer() {
  const [algoliaModules, setAlgoliaModules] = useState<AlgoliaModules | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadAlgolia = async () => {
      try {
        const [algoliaJs, algoliaApi] = await Promise.all([
          import(/* webpackChunkName: "algolia-search" */ '@algolia/autocomplete-js'),
          import(/* webpackChunkName: "algolia-search" */ '../../Api/Search/Algolia'),
        ])

        setAlgoliaModules({
          getAlgoliaResults: algoliaJs.getAlgoliaResults,
          searchClient: algoliaApi.searchClient,
        })
        setIsLoading(false)
      } catch (error) {
        console.warn('Failed to load Algolia search:', error)
        setIsLoading(false)
      }
    }

    loadAlgolia()
  }, [])

  if (isLoading || !algoliaModules) {
    return (
      <SearchLoadingContainer>
        <SearchLoadingText>Loading...</SearchLoadingText>
      </SearchLoadingContainer>
    )
  }

  const { getAlgoliaResults, searchClient } = algoliaModules

  // Create debounced search function
  const debouncedSearch = debouncePromise((searchClient: any, queries: any[]) => {
    return getAlgoliaResults({
      searchClient,
      queries,
    })
  }, 300)

  return (
    <Autocomplete
      openOnFocus={true}
      getSources={() => [
        {
          sourceId: 'pll',
          getItems({ query }: { query: string }) {
            if (query.length < 1) return []
            return debouncedSearch(searchClient, [
              {
                indexName: SEARCH_CONFIG.pll,
                query,
                params: {
                  hitsPerPage: 2,
                  clickAnalytics: true,
                },
              },
              {
                indexName: SEARCH_CONFIG.teams,
                query,
                params: {
                  hitsPerPage: 2,
                  clickAnalytics: true,
                },
              },
              {
                indexName: SEARCH_CONFIG.players,
                query,
                params: {
                  hitsPerPage: 2,
                  clickAnalytics: true,
                },
              },
              {
                indexName: SEARCH_CONFIG.posts,
                query,
                params: {
                  hitsPerPage: 2,
                  clickAnalytics: true,
                },
              },
              {
                indexName: SEARCH_CONFIG.videos,
                query,
                params: {
                  hitsPerPage: 2,
                  clickAnalytics: true,
                },
              },
            ])
          },
          templates: {
            header() {
              return (
                <div>
                  <span className="aa-SourceHeaderTitle">Search Results</span>
                  <div className="aa-SourceHeaderLine" />
                </div>
              )
            },
            item({ item, components }: { item: any; components: any }) {
              if (item?.type && (item.type === 'article' || item.type === 'video')) {
                return <PLLContentSearchResult hit={item} components={components} />
              } else {
                return <PLLSearchResult hit={item} components={components} />
              }
            },
            noResults() {
              return 'No results'
            },
          },
        },
        {
          sourceId: 'products',
          getItems({ query }: { query: string }) {
            if (query.length < 1) return []
            return debouncedSearch(searchClient, [
              {
                indexName: SEARCH_CONFIG.shop,
                query,
                params: {
                  hitsPerPage: 5,
                  clickAnalytics: true,
                },
              },
            ])
          },
          templates: {
            header() {
              return (
                <div>
                  <span className="aa-SourceHeaderTitle">Shop Results</span>
                  <div className="aa-SourceHeaderLine" />
                </div>
              )
            },
            item({ item, components }: { item: any; components: any }) {
              return <ShopifySearchResult hit={item} components={components} />
            },
          },
        },
      ]}
    />
  )
}
